import { assetKeyFromUrl } from './assetCdnUrl.js';

export { assetKeyFromUrl as s3KeyFromUrl } from './assetCdnUrl.js';

function asDownloadableUrl(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  return /^(https?:)?\/\//i.test(s) ? s : '';
}

function firstDownloadableUrl(values = []) {
  for (const v of values) {
    const u = asDownloadableUrl(v);
    if (u) return u;
  }
  return '';
}

const s3KeyFromUrl = assetKeyFromUrl;

function normalizeTicketFiles(raw) {
  const base = raw && typeof raw === 'object' ? raw : {};
  return {
    departure: Array.isArray(base.departure) ? base.departure : [],
    return: Array.isArray(base.return) ? base.return : []
  };
}

function firstDownloadableUrlFromNode(node) {
  if (!node) return '';
  if (typeof node === 'string') return asDownloadableUrl(node);
  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = firstDownloadableUrlFromNode(item);
      if (hit) return hit;
    }
    return '';
  }
  if (typeof node === 'object') {
    return firstDownloadableUrl([
      node.url,
      node.fileUrl,
      node.downloadUrl,
      node.ticketUrl,
      node.ticket_url
    ]);
  }
  return '';
}

function extractTicketUrlFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return '';

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = extractTicketUrlFromPayload(item);
      if (nested) return nested;
    }
    return '';
  }

  const direct = firstDownloadableUrl([
    payload.url,
    payload.fileUrl,
    payload.downloadUrl,
    payload.ticketUrl,
    payload.ticket_url,
    payload.returnTicketUrl,
    payload.return_ticket_url,
    payload.departureTicketUrl,
    payload.departure_ticket_url
  ]);
  if (direct) return direct;

  const knownNodes = [
    payload.departure,
    payload.return,
    payload.tickets,
    payload.ticketFiles,
    payload.ticket_files,
    payload.ticket,
    payload.ticketLinks,
    payload.ticket_links
  ];
  for (const node of knownNodes) {
    const nested = extractTicketUrlFromPayload(node);
    if (nested) return nested;
  }
  return '';
}

function extractLegUrlFromPayload(payload, leg) {
  if (!payload || typeof payload !== 'object') return '';
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const hit = extractLegUrlFromPayload(item, leg);
      if (hit) return hit;
    }
    return '';
  }

  if (leg === 'departure') {
    const direct = firstDownloadableUrl([
      payload.departureTicketUrl,
      payload.departure_ticket_url
    ]);
    if (direct) return direct;
    if (payload.departure) {
      const fromNode = firstDownloadableUrlFromNode(payload.departure);
      if (fromNode) return fromNode;
    }
  }

  if (leg === 'return') {
    const direct = firstDownloadableUrl([
      payload.returnTicketUrl,
      payload.return_ticket_url
    ]);
    if (direct) return direct;
    if (payload.return) {
      const fromNode = firstDownloadableUrlFromNode(payload.return);
      if (fromNode) return fromNode;
    }
  }

  return '';
}

/** First downloadable ticket URL on a booking (any leg). */
export function bookingTicketUrl(booking) {
  if (!booking) return '';
  const options = resolveBookingTicketDownloadOptions(booking);
  if (options.length) return options[0].url || '';
  const directResolved = firstDownloadableUrl([booking.ticketDownloadUrl]);
  if (directResolved) return directResolved;
  const direct = firstDownloadableUrl([
    booking.ticketUrl,
    booking.ticket_url,
    booking.ticketFileUrl,
    booking.ticket_file_url
  ]);
  if (direct) return direct;
  const batchJson =
    booking?.batch?.showTicketsToParticipantsJson ??
    booking?.batch?.show_tickets_to_participants_json ??
    null;
  const fromBatch = extractTicketUrlFromPayload(batchJson);
  if (fromBatch) return fromBatch;
  return extractTicketUrlFromPayload(booking);
}

/**
 * Departure + return ticket download targets for the participant dashboard.
 * @returns {Array<{ id: string, leg: string, label: string, bookingId: string, url: string, key: string, fileId: string }>}
 */
export function resolveBookingTicketDownloadOptions(booking) {
  if (!booking?.id) return [];

  const bookingId = booking.id;
  const options = [];
  const legsSeen = new Set();

  const addOption = (leg, source) => {
    const url = firstDownloadableUrl([source?.url]);
    const key = String(source?.key || s3KeyFromUrl(url) || '').trim();
    if (!url && !key) return;
    const sig = `${leg}|${key || url}`;
    if (legsSeen.has(sig)) return;
    legsSeen.add(sig);
    const label =
      leg === 'departure'
        ? 'Download Departure Ticket'
        : leg === 'return'
          ? 'Download Return Ticket'
          : 'Download Ticket';
    options.push({
      id: sig,
      leg,
      label,
      bookingId,
      url,
      key,
      fileId: String(source?.fileId || '').trim()
    });
  };

  const ticketFiles = normalizeTicketFiles(booking.ticketFiles || booking.ticket_files);
  for (const leg of ['departure', 'return']) {
    const list = ticketFiles[leg] || [];
    const first = list.find((f) => f?.url || f?.key);
    if (first) {
      addOption(leg, {
        url: first.url,
        key: first.key || s3KeyFromUrl(first.url),
        fileId: first.id
      });
    }
  }

  const batchJson =
    booking?.batch?.showTicketsToParticipantsJson ??
    booking?.batch?.show_tickets_to_participants_json ??
    null;
  for (const leg of ['departure', 'return']) {
    if (options.some((o) => o.leg === leg)) continue;
    const url = extractLegUrlFromPayload(batchJson, leg);
    if (url) addOption(leg, { url, key: s3KeyFromUrl(url) });
  }

  if (options.length) return options;

  const generic = firstDownloadableUrl([
    booking.ticketDownloadUrl,
    booking.ticketUrl,
    booking.ticket_url,
    booking.ticketFileUrl,
    booking.ticket_file_url,
    extractTicketUrlFromPayload(batchJson),
    extractTicketUrlFromPayload(booking)
  ]);
  if (generic) addOption('ticket', { url: generic, key: s3KeyFromUrl(generic) });

  return options;
}
